import { useHandler } from "@redotech/react-util/hook";
import {
  OmsCustomAttributeDate1ConditionStep,
  OmsCustomAttributeDate2ConditionStep,
  OmsCustomAttributeDate3ConditionStep,
  OmsCustomDate1ConditionStep,
  OmsCustomDate2ConditionStep,
  OmsCustomDate3ConditionStep,
  OmsProductCustomDate1ConditionStep,
  OmsProductCustomDate2ConditionStep,
  OmsProductCustomDate3ConditionStep,
} from "@redotech/redo-flows-common/oms/conditions";
import { OmsConditionStep } from "@redotech/redo-flows-common/steps";
import { useTranslation } from "@redotech/redo-i18n";
import { formatDateString } from "@redotech/redo-merchant-fulfillment-app/utils/outbound-labels-utils";
import { DateFilterOperator } from "@redotech/redo-views-common/advanced-filters/date-filter";
import { CustomDatePickerDropdown } from "@redotech/redo-web/advanced-filters/components/date-filter";
import { RedoFilterDropdownAnchor } from "@redotech/redo-web/arbiter-components/filter-group/redo-filter-group";
import { assertNever } from "@redotech/util/type";
import { memo, useMemo, useState } from "react";
import { DeepPartial } from "..";

export const OmsDatePickerInput = memo(function DatePickerInput({
  step,
  setStep,
}: {
  step:
    | DeepPartial<OmsCustomDate1ConditionStep>
    | DeepPartial<OmsCustomDate2ConditionStep>
    | DeepPartial<OmsCustomDate3ConditionStep>
    | DeepPartial<OmsCustomAttributeDate1ConditionStep>
    | DeepPartial<OmsCustomAttributeDate2ConditionStep>
    | DeepPartial<OmsCustomAttributeDate3ConditionStep>
    | DeepPartial<OmsProductCustomDate1ConditionStep>
    | DeepPartial<OmsProductCustomDate2ConditionStep>
    | DeepPartial<OmsProductCustomDate3ConditionStep>;
  setStep: (step: DeepPartial<OmsConditionStep>) => void;
}) {
  const { t } = useTranslation();
  const [valueRef, setValueRef] = useState<HTMLButtonElement | null>(null);
  const [customDateDropdownOpen, setCustomDateDropdownOpen] = useState(false);
  const setMatchValue = useHandler((matchDate: [Date]) => {
    setStep({ ...step, matchDate: matchDate[0] });
  });

  const operator = useMemo(() => {
    switch (step.matchType) {
      case undefined:
      case "before":
        return DateFilterOperator.BEFORE;
      case "after":
        return DateFilterOperator.AFTER;
      default:
        assertNever(step.matchType);
    }
  }, [step.matchType]);

  return (
    <>
      <RedoFilterDropdownAnchor
        color="primary"
        onClick={() => setCustomDateDropdownOpen(!customDateDropdownOpen)}
        ref={setValueRef}
        text={`${step.matchDate ? formatDateString((step.matchDate as Date).toISOString(), true) : t("Choose a date")}`}
        weight="medium"
      />
      <CustomDatePickerDropdown
        allowCustomizeTimeOfDay={false}
        allowFutureDates
        anchor={valueRef}
        customDate={[(step.matchDate as Date) ?? new Date()]}
        open={customDateDropdownOpen}
        operator={operator}
        setCustomDate={setMatchValue}
        setOpen={setCustomDateDropdownOpen}
      />
    </>
  );
});
